import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Link2, Pencil, Plus, Swords, Trash2, Unlink } from 'lucide-react';
import { Heading } from '../../layout';
import { FormDrawer } from '../../forms';
import { useUIColors } from '../../../hooks/useTheme';
import { useSectionAccess } from '../../../hooks/useSectionAccess';
import { paintCompetitorsAPI } from '../../../services/paintCompetitorsAPI';
import ProductionDataTable, { ProductionInnerTabs } from '../../placeholders/ProductionDataTable';
import {
  collectCompetitorApplications,
  collectCompetitorCategories,
  collectCompetitorChemistries,
} from './paintCompetitorCatalog';

const TAB_STORAGE_KEY = 'sqmt_paint_competitors_tab';

const RELATION_OPTIONS = [
  { value: 'homologo', label: 'Homólogo' },
  { value: 'simil', label: 'Símil' },
  { value: 'reemplazo', label: 'Reemplazo' },
];

const RELATION_TONE = {
  homologo: { bg: '#dcfce7', color: '#166534', darkBg: '#14532d', darkColor: '#86efac' },
  simil: { bg: '#fef3c7', color: '#92400e', darkBg: '#422006', darkColor: '#fcd34d' },
  reemplazo: { bg: '#dbeafe', color: '#1d4ed8', darkBg: '#1e3a8a', darkColor: '#93c5fd' },
};

const relationLabel = (value) => RELATION_OPTIONS.find((item) => item.value === value)?.label || '';

const RelationBadge = ({ value, isDarkMode }) => {
  const tone = RELATION_TONE[value] || RELATION_TONE.homologo;
  const label = relationLabel(value) || '—';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        background: isDarkMode ? tone.darkBg : tone.bg,
        color: isDarkMode ? tone.darkColor : tone.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

const RelationSelect = ({ value, onChange, disabled, isDarkMode }) => {
  const current = value || 'homologo';
  const tone = RELATION_TONE[current] || RELATION_TONE.homologo;
  return (
    <select
      value={current}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => {
        event.stopPropagation();
        onChange(event.target.value);
      }}
      style={{
        height: 26,
        padding: '0 8px',
        borderRadius: 999,
        border: 'none',
        background: isDarkMode ? tone.darkBg : tone.bg,
        color: isDarkMode ? tone.darkColor : tone.color,
        fontSize: 11,
        fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {RELATION_OPTIONS.map((item) => (
        <option key={item.value} value={item.value}>
          {item.label}
        </option>
      ))}
    </select>
  );
};

const tableCell = (children, align = 'left') => (
  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: align, verticalAlign: 'top' }}>{children}</td>
);

const emptyDistributor = () => ({
  name: '',
  sale_price: '',
  currency: 'CLP',
  website_url: '',
  notes: '',
});

const emptyProduct = () => ({
  brand: '',
  product_name: '',
  category: '',
  chemistry: '',
  summary: '',
  solids: '',
  voc: '',
  tack_dry: '',
  hard_dry: '',
  url: '',
  applications: [],
  distributors: [],
});

const formatMoneyClp = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `$ ${Math.round(n).toLocaleString('es-CL')}`;
};

const formatPriceRange = (min, max) => {
  const low = Number(min);
  const high = Number(max);
  if (!Number.isFinite(low) && !Number.isFinite(high)) return '';
  if (Number.isFinite(low) && Number.isFinite(high) && low === high) return formatMoneyClp(low);
  if (Number.isFinite(low) && Number.isFinite(high)) {
    return `${formatMoneyClp(low)} – ${formatMoneyClp(high)}`;
  }
  return formatMoneyClp(Number.isFinite(low) ? low : high);
};

const priceRangeFromDistributors = (distributors = []) => {
  const prices = (distributors || [])
    .map((row) => Number(row?.sale_price))
    .filter((value) => Number.isFinite(value));
  if (!prices.length) {
    return { price_min: null, price_max: null, priced_count: 0, distributor_count: (distributors || []).length };
  }
  return {
    price_min: Math.min(...prices),
    price_max: Math.max(...prices),
    priced_count: prices.length,
    distributor_count: (distributors || []).length,
  };
};

const chipStyle = (active, ui) => ({
  height: 28,
  padding: '0 10px',
  borderRadius: 999,
  border: `1px solid ${active ? ui.interactive || '#16a34a' : ui.border}`,
  background: active ? ui.interactive || '#16a34a' : 'transparent',
  color: active ? ui.textOnAccent || '#fff' : ui.text,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
});

const ChipPicker = ({ label, value = [], options = [], onChange, ui, allowCustom = false, placeholder = 'Otra…' }) => {
  const [draft, setDraft] = useState('');
  const selected = Array.isArray(value) ? value : [];
  const toggle = (item) => {
    onChange(selected.includes(item) ? selected.filter((entry) => entry !== item) : [...selected, item]);
  };
  const addDraft = () => {
    const next = draft.trim();
    if (!next) return;
    if (!selected.includes(next)) onChange([...selected, next]);
    setDraft('');
  };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: ui.text }}>{label}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map((item) => {
          const active = selected.includes(item);
          return (
            <button key={item} type="button" onClick={() => toggle(item)} style={chipStyle(active, ui)}>
              {item}
            </button>
          );
        })}
      </div>
      {allowCustom ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addDraft();
              }
            }}
            placeholder={placeholder}
            style={{
              flex: 1,
              height: 34,
              padding: '0 10px',
              borderRadius: 8,
              border: `1px solid ${ui.inputBorder || ui.border}`,
              background: ui.inputBg || ui.panel,
              color: ui.inputText || ui.text,
              fontSize: 13,
            }}
          />
          <button type="button" onClick={addDraft} style={chipStyle(false, ui)}>
            Agregar
          </button>
        </div>
      ) : null}
    </div>
  );
};

const DatalistField = ({ label, value, onChange, options, placeholder, ui, listId }) => (
  <div style={{ display: 'grid', gap: 6 }}>
    {label ? <div style={{ fontSize: 12, fontWeight: 600, color: ui.text }}>{label}</div> : null}
    <input
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      list={listId}
      placeholder={placeholder}
      style={{
        height: 34,
        padding: '0 10px',
        borderRadius: 8,
        border: `1px solid ${ui.inputBorder || ui.border}`,
        background: ui.inputBg || ui.panel,
        color: ui.inputText || ui.text,
        fontSize: 13,
      }}
    />
    <datalist id={listId}>
      {options.map((item) => (
        <option key={item} value={item} />
      ))}
    </datalist>
  </div>
);

const renderChipGroup = (values, ui, emptyLabel = '—') => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {(values || []).length ? (
      values.map((value) => (
        <span
          key={value}
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 999,
            border: `1px solid ${ui.border}`,
            color: ui.text,
          }}
        >
          {value}
        </span>
      ))
    ) : (
      <span style={{ fontSize: 12, color: ui.muted }}>{emptyLabel}</span>
    )}
  </div>
);

const normalizeProduct = (item = {}) => {
  const distributors = Array.isArray(item.distributors)
    ? item.distributors.map((row) => ({
        ...row,
        sale_price: row?.sale_price == null || row?.sale_price === '' ? '' : Number(row.sale_price),
      }))
    : [];
  const range = priceRangeFromDistributors(distributors);
  const priceMin = item.price_min != null ? Number(item.price_min) : range.price_min;
  const priceMax = item.price_max != null ? Number(item.price_max) : range.price_max;
  return {
    ...item,
    applications: Array.isArray(item.applications) ? item.applications.filter(Boolean) : [],
    applicationsLabel: Array.isArray(item.applications) ? item.applications.filter(Boolean).join(' ') : '',
    distributors,
    price_min: priceMin,
    price_max: priceMax,
    priceRangeLabel: formatPriceRange(priceMin, priceMax),
    distributor_count: Number(item.distributor_count ?? distributors.length) || 0,
  };
};

const DistributorsEditor = ({ value = [], onChange, ui, readOnly = false }) => {
  const rows = Array.isArray(value) && value.length ? value : [emptyDistributor()];
  const updateRow = (index, patch) => {
    const next = rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
    onChange(next);
  };
  const addRow = () => onChange([...rows, emptyDistributor()]);
  const removeRow = (index) => {
    const next = rows.filter((_, rowIndex) => rowIndex !== index);
    onChange(next.length ? next : []);
  };
  const fieldStyle = {
    width: '100%',
    height: 34,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder || ui.border}`,
    background: ui.inputBg || 'transparent',
    color: ui.text,
    fontSize: 13,
    boxSizing: 'border-box',
  };
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: ui.text }}>Distribuidores</div>
          <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>
            Precio de venta y enlace por proveedor del producto.
          </div>
        </div>
        {!readOnly ? (
          <button
            type="button"
            onClick={addRow}
            style={{
              height: 30,
              borderRadius: 8,
              border: `1px solid ${ui.border}`,
              background: 'transparent',
              color: ui.text,
              padding: '0 10px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Plus size={13} /> Agregar
          </button>
        ) : null}
      </div>
      {rows.map((row, index) => (
        <div
          key={`distributor-${index}`}
          style={{
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            padding: 10,
            display: 'grid',
            gap: 8,
            background: ui.header || 'transparent',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 0.9fr) auto', gap: 8 }}>
            <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: ui.muted }}>Distribuidor</span>
              <input
                value={row.name || ''}
                disabled={readOnly}
                onChange={(event) => updateRow(index, { name: event.target.value })}
                placeholder="Nombre del proveedor"
                style={fieldStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: ui.muted }}>Precio venta</span>
              <input
                type="number"
                min="0"
                step="1"
                value={row.sale_price === '' || row.sale_price == null ? '' : row.sale_price}
                disabled={readOnly}
                onChange={(event) => updateRow(index, { sale_price: event.target.value })}
                placeholder="0"
                style={fieldStyle}
              />
            </label>
            {!readOnly ? (
              <button
                type="button"
                title="Quitar"
                onClick={() => removeRow(index)}
                style={{
                  alignSelf: 'end',
                  height: 34,
                  width: 34,
                  borderRadius: 8,
                  border: `1px solid ${ui.border}`,
                  background: 'transparent',
                  color: ui.muted,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Trash2 size={14} />
              </button>
            ) : (
              <span />
            )}
          </div>
          <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: ui.muted }}>Enlace web</span>
            <input
              value={row.website_url || ''}
              disabled={readOnly}
              onChange={(event) => updateRow(index, { website_url: event.target.value })}
              placeholder="https://…"
              style={fieldStyle}
            />
          </label>
        </div>
      ))}
    </div>
  );
};

const PaintCompetitorsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const access = useSectionAccess();
  const [tab, setTab] = useState(() => {
    try {
      return window.localStorage.getItem(TAB_STORAGE_KEY) === 'compare' ? 'compare' : 'products';
    } catch {
      return 'products';
    }
  });
  const [products, setProducts] = useState([]);
  const [tcoat, setTcoat] = useState([]);
  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyProduct());
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTcoatId, setSelectedTcoatId] = useState(null);
  const [selectedCompetitorId, setSelectedCompetitorId] = useState(null);
  const [tcoatQuery, setTcoatQuery] = useState('');
  const [competitorQuery, setCompetitorQuery] = useState('');
  const [linkError, setLinkError] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [draftRelation, setDraftRelation] = useState('homologo');

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const [rows, tcoatRows, matchRows] = await Promise.all([
        paintCompetitorsAPI.listProducts(),
        paintCompetitorsAPI.listTcoat(),
        paintCompetitorsAPI.listMatches(),
      ]);
      setProducts(Array.isArray(rows) ? rows.map(normalizeProduct) : []);
      setTcoat(Array.isArray(tcoatRows) ? tcoatRows : []);
      setMatches(Array.isArray(matchRows) ? matchRows : []);
    } catch (err) {
      setProducts([]);
      setTcoat([]);
      setMatches([]);
      setError(err?.message || 'No se pudieron cargar los competidores.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {
      /* ignore */
    }
  }, [tab]);

  const matchesByTcoat = useMemo(() => {
    const map = new Map();
    matches.forEach((row) => {
      const key = Number(row.paint_product_id);
      const list = map.get(key) || [];
      list.push(row);
      map.set(key, list);
    });
    return map;
  }, [matches]);

  const matchForPair = useCallback(
    (paintId, competitorId) =>
      matches.find(
        (row) => Number(row.paint_product_id) === Number(paintId) && Number(row.competitor_id) === Number(competitorId)
      ) || null,
    [matches]
  );

  const relationFor = useCallback(
    (competitorId) => matchForPair(selectedTcoatId, competitorId)?.relation || '',
    [matchForPair, selectedTcoatId]
  );

  const selectedTcoat = useMemo(
    () => tcoat.find((item) => Number(item.id) === Number(selectedTcoatId)) || null,
    [tcoat, selectedTcoatId]
  );
  const selectedCompetitor = useMemo(
    () => products.find((item) => Number(item.id) === Number(selectedCompetitorId)) || null,
    [products, selectedCompetitorId]
  );

  const visibleTcoat = useMemo(() => {
    const tokens = tcoatQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return tcoat.filter((item) => {
      if (!tokens.length) return true;
      const hay = `${item.sku || ''} ${item.product_name || ''}`.toLowerCase();
      return tokens.every((token) => hay.includes(token));
    });
  }, [tcoat, tcoatQuery]);

  const homologatedIds = useMemo(() => {
    const set = new Set(
      (matchesByTcoat.get(Number(selectedTcoatId)) || []).map((row) => Number(row.competitor_id))
    );
    return set;
  }, [matchesByTcoat, selectedTcoatId]);

  const visibleCompetitors = useMemo(() => {
    const tokens = competitorQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const filtered = products.filter((item) => {
      if (!selectedTcoatId) return false;
      if (tokens.length) {
        const hay = `${item.brand || ''} ${item.product_name || ''} ${item.category || ''} ${item.chemistry || ''} ${item.applicationsLabel || ''}`.toLowerCase();
        if (!tokens.every((token) => hay.includes(token))) return false;
      } else if (!homologatedIds.has(Number(item.id))) {
        return false;
      }
      return true;
    });
    return filtered.sort((a, b) => {
      const aLinked = homologatedIds.has(Number(a.id)) ? 0 : 1;
      const bLinked = homologatedIds.has(Number(b.id)) ? 0 : 1;
      if (aLinked !== bLinked) return aLinked - bLinked;
      return `${a.brand} ${a.product_name}`.localeCompare(`${b.brand} ${b.product_name}`, 'es');
    });
  }, [products, selectedTcoatId, competitorQuery, homologatedIds]);

  useEffect(() => {
    if (!selectedTcoatId && visibleTcoat.length) {
      setSelectedTcoatId(visibleTcoat[0].id);
    }
  }, [selectedTcoatId, visibleTcoat]);

  useEffect(() => {
    if (!selectedTcoatId) {
      setSelectedCompetitorId(null);
      return;
    }
    const linked = (matchesByTcoat.get(Number(selectedTcoatId)) || [])[0];
    if (linked) {
      setSelectedCompetitorId(linked.competitor_id);
      return;
    }
    setSelectedCompetitorId(null);
  }, [selectedTcoatId, matchesByTcoat]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyProduct());
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      brand: item.brand || '',
      product_name: item.product_name || '',
      category: item.category || '',
      chemistry: item.chemistry || '',
      summary: item.summary || '',
      solids: item.solids || '',
      voc: item.voc || '',
      tack_dry: item.tack_dry || '',
      hard_dry: item.hard_dry || '',
      url: item.url || '',
      applications: Array.isArray(item.applications) ? item.applications : [],
      distributors: Array.isArray(item.distributors)
        ? item.distributors.map((row) => ({
            name: row.name || '',
            sale_price: row.sale_price == null || row.sale_price === '' ? '' : String(row.sale_price),
            currency: row.currency || 'CLP',
            website_url: row.website_url || '',
            notes: row.notes || '',
          }))
        : [],
    });
    setFormError('');
    setFormOpen(true);
  };

  const saveForm = async () => {
    try {
      setIsSaving(true);
      setFormError('');
      const payload = {
        ...form,
        distributors: (form.distributors || [])
          .map((row) => ({
            name: String(row.name || '').trim(),
            sale_price:
              row.sale_price === '' || row.sale_price == null ? null : Number(row.sale_price),
            currency: row.currency || 'CLP',
            website_url: String(row.website_url || '').trim(),
            notes: String(row.notes || '').trim(),
          }))
          .filter((row) => row.name || row.sale_price != null || row.website_url),
      };
      if (editingId) {
        await paintCompetitorsAPI.updateProduct(editingId, payload);
      } else {
        await paintCompetitorsAPI.createProduct(payload);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar el producto.');
    } finally {
      setIsSaving(false);
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`¿Eliminar “${item.brand} ${item.product_name}”?`)) return;
    try {
      await paintCompetitorsAPI.removeProduct(item.id);
      await load();
    } catch (err) {
      window.alert(err?.message || 'No se pudo eliminar el producto.');
    }
  };

  const toggleHomologation = async () => {
    if (!selectedTcoat || !selectedCompetitor) return;
    const existing = matchForPair(selectedTcoat.id, selectedCompetitor.id);
    try {
      setIsLinking(true);
      setLinkError('');
      if (existing) {
        await paintCompetitorsAPI.removeMatch(existing.id);
      } else {
        await paintCompetitorsAPI.createMatch({
          paint_product_id: Number(selectedTcoat.id),
          competitor_id: Number(selectedCompetitor.id),
          relation: draftRelation || 'homologo',
        });
      }
      await load();
    } catch (err) {
      setLinkError(err?.message || 'No se pudo actualizar la homologación.');
    } finally {
      setIsLinking(false);
    }
  };

  const changeRelation = async (matchId, relation) => {
    if (!matchId || !relation) return;
    try {
      setIsLinking(true);
      setLinkError('');
      await paintCompetitorsAPI.updateMatch(matchId, { relation });
      await load();
    } catch (err) {
      setLinkError(err?.message || 'No se pudo actualizar la relación.');
    } finally {
      setIsLinking(false);
    }
  };

  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive || '#16a34a',
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
  };

  const box = {
    border: `1px solid ${ui.border}`,
    borderRadius: 10,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: ui.panel,
  };
  const boxHeader = {
    padding: '10px 12px',
    borderBottom: `1px solid ${ui.border}`,
    background: ui.header,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  };
  const th = (align = 'left') => ({
    position: 'sticky',
    top: 0,
    background: ui.header,
    textAlign: align,
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: ui.muted,
    borderBottom: `1px solid ${ui.border}`,
    whiteSpace: 'nowrap',
    zIndex: 1,
  });
  const td = (align = 'left') => ({
    textAlign: align,
    padding: '8px 10px',
    fontSize: 12,
    color: ui.text,
    borderTop: `1px solid ${ui.border}`,
    verticalAlign: 'top',
  });
  const selectedBg = isDarkMode ? 'rgba(22, 163, 74, 0.18)' : '#f0fdf4';
  const inputStyle = {
    width: '100%',
    height: 34,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder || ui.border}`,
    background: ui.inputBg || 'transparent',
    color: ui.text,
    fontSize: 13,
    boxSizing: 'border-box',
  };

  const formSections = [
    {
      key: 'main',
      title: 'Producto competidor',
      defaultOpen: true,
      fields: [
        { key: 'brand', label: 'Marca' },
        { key: 'product_name', label: 'Producto' },
        {
          key: 'category',
          label: 'Categoría',
          type: 'custom',
          render: ({ value, onChange }) => (
            <DatalistField
              label="Categoría"
              value={value}
              onChange={onChange}
              options={collectCompetitorCategories([{ category: value }, ...products])}
              placeholder="Categoría"
              ui={ui}
              listId="paint-competitor-categories"
            />
          ),
        },
        {
          key: 'chemistry',
          label: 'Química',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange }) => (
            <DatalistField
              label="Química"
              value={value}
              onChange={onChange}
              options={collectCompetitorChemistries([{ chemistry: value }, ...products])}
              placeholder="Química de producto"
              ui={ui}
              listId="paint-competitor-chemistries"
            />
          ),
        },
        { key: 'summary', label: 'Descripción', type: 'textarea', rows: 5, fullWidth: true },
        { key: 'solids', label: 'Sólidos' },
        { key: 'voc', label: 'VOC' },
        { key: 'tack_dry', label: 'Secado tacto' },
        { key: 'hard_dry', label: 'Secado duro' },
        { key: 'url', label: 'Ficha técnica', fullWidth: true },
        {
          key: 'applications',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange }) => (
            <ChipPicker
              label="Aplicación"
              value={value}
              onChange={onChange}
              options={collectCompetitorApplications([{ applications: value }, ...products])}
              ui={ui}
              allowCustom
              placeholder="Otra aplicación…"
            />
          ),
        },
        {
          key: 'distributors',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange }) => (
            <DistributorsEditor
              value={value}
              onChange={onChange}
              ui={ui}
              readOnly={editingId ? !access.canUpdate : !access.canCreate}
            />
          ),
        },
      ],
    },
  ];

  const linkedMatch =
    selectedTcoat && selectedCompetitor ? matchForPair(selectedTcoat.id, selectedCompetitor.id) : null;
  const linked = Boolean(linkedMatch);
  const selectedRelation = linkedMatch?.relation || '';
  const linkedCompetitorIds = new Set(
    (matchesByTcoat.get(Number(selectedTcoatId)) || []).map((row) => Number(row.competitor_id))
  );
  const linkedCompetitorsForTcoat = products.filter((item) => linkedCompetitorIds.has(Number(item.id)));
  const linkedPrices = linkedCompetitorsForTcoat.flatMap((item) =>
    (item.distributors || [])
      .map((row) => Number(row.sale_price))
      .filter((value) => Number.isFinite(value))
  );
  const linkedPriceRange = linkedPrices.length
    ? {
        label: formatPriceRange(Math.min(...linkedPrices), Math.max(...linkedPrices)),
        count: linkedPrices.length,
        products: linkedCompetitorsForTcoat.length,
      }
    : { label: '', count: 0, products: linkedCompetitorsForTcoat.length };
  const selectedCompetitorPriceLabel = selectedCompetitor?.priceRangeLabel || '';
  const comparisonRows = [
    { label: 'Marca', left: 'T-COAT', right: selectedCompetitor?.brand },
    { label: 'Producto', left: selectedTcoat?.product_name, right: selectedCompetitor?.product_name },
    { label: 'Código', left: selectedTcoat?.sku, right: '—' },
    { label: 'Relación', left: '—', right: relationLabel(selectedRelation) || (linked ? 'Homólogo' : '—') },
    {
      label: 'Rango precios (homólogos / sustitutos)',
      left: '—',
      right: linkedPriceRange.label
        ? `${linkedPriceRange.label} · ${linkedPriceRange.products} opción${linkedPriceRange.products === 1 ? '' : 'es'}`
        : 'Sin precios cargados',
    },
    {
      label: 'Precio producto seleccionado',
      left: '—',
      right: selectedCompetitorPriceLabel || 'Sin distribuidores con precio',
    },
    { label: 'Categoría', left: '—', right: selectedCompetitor?.category },
    { label: 'Química', left: '—', right: selectedCompetitor?.chemistry },
    {
      label: 'Aplicación',
      left: '—',
      right: (selectedCompetitor?.applications || []).join(', '),
    },
    { label: 'Sólidos', left: '—', right: selectedCompetitor?.solids },
    { label: 'VOC', left: '—', right: selectedCompetitor?.voc },
    { label: 'Secado tacto', left: '—', right: selectedCompetitor?.tack_dry },
    { label: 'Secado duro', left: '—', right: selectedCompetitor?.hard_dry },
  ];

  const renderProductsTab = () => (
    <ProductionDataTable
      title="Productos de la competencia"
      subtitle={`${products.length} producto${products.length === 1 ? '' : 's'}`}
      rows={products}
      columns={[
        { key: 'Marca', sortKey: 'brand' },
        { key: 'Producto', sortKey: 'product_name' },
        { key: 'Categoría', sortKey: 'category' },
        { key: 'Química', sortKey: 'chemistry' },
        { key: 'Aplicación', sortKey: 'applicationsLabel' },
        { key: 'Precio', sortKey: 'price_min' },
        { key: 'Sólidos', sortKey: 'solids' },
        { key: 'VOC', sortKey: 'voc' },
        { key: '', sortKey: '' },
      ]}
      defaultSortKey="brand"
      searchPlaceholder="Buscar marca, producto, química o aplicación…"
      onRowClick={access.canUpdate ? openEdit : undefined}
      filters={[
        { key: 'category', label: 'Categoría' },
        { key: 'chemistry', label: 'Química' },
        {
          key: 'application',
          label: 'Aplicación',
          getOptions: (row) => row.applications || [],
          matches: (row, selected) => (row.applications || []).includes(selected),
        },
      ]}
      action={
        access.canCreate ? (
          <button type="button" onClick={openCreate} style={primaryBtn}>
            <Plus size={14} />
            Nuevo producto
          </button>
        ) : null
      }
      renderCells={(item) => (
        <>
          {tableCell(<strong>{item.brand || '—'}</strong>)}
          {tableCell(
            <div>
              <div>{item.product_name || '—'}</div>
              {item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  style={{ fontSize: 11, color: ui.interactive || '#16a34a', display: 'inline-flex', gap: 4, alignItems: 'center' }}
                >
                  Ficha <ExternalLink size={11} />
                </a>
              ) : null}
            </div>
          )}
          {tableCell(item.category || '—')}
          {tableCell(item.chemistry || '—')}
          {tableCell(renderChipGroup(item.applications, ui))}
          {tableCell(
            <div>
              <div style={{ fontWeight: 600 }}>{item.priceRangeLabel || '—'}</div>
              {item.distributor_count ? (
                <div style={{ fontSize: 11, color: ui.muted }}>
                  {item.distributor_count} distribuidor{item.distributor_count === 1 ? '' : 'es'}
                </div>
              ) : null}
            </div>
          )}
          {tableCell(item.solids || '—')}
          {tableCell(item.voc || '—')}
          {tableCell(
            <div style={{ display: 'inline-flex', gap: 6 }}>
              {access.canUpdate ? (
                <button
                  type="button"
                  title="Editar"
                  onClick={(event) => {
                    event.stopPropagation();
                    openEdit(item);
                  }}
                  style={ghostBtn}
                >
                  <Pencil size={13} />
                </button>
              ) : null}
              {access.canDelete ? (
                <button
                  type="button"
                  title="Eliminar"
                  onClick={(event) => {
                    event.stopPropagation();
                    removeItem(item);
                  }}
                  style={ghostBtn}
                >
                  <Trash2 size={13} />
                </button>
              ) : null}
            </div>,
            'right'
          )}
        </>
      )}
    />
  );

  const renderCompareTab = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.25fr)',
        gap: 12,
        minHeight: 520,
        height: '68vh',
      }}
    >
      <section style={box}>
        <div style={boxHeader}>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text }}>Productos T-COAT</h4>
          <span style={{ fontSize: 12, color: ui.muted }}>{visibleTcoat.length}</span>
        </div>
        <div style={{ padding: '10px 12px', borderBottom: `1px solid ${ui.border}` }}>
          <input
            value={tcoatQuery}
            onChange={(event) => setTcoatQuery(event.target.value)}
            placeholder="Buscar SKU o nombre…"
            style={inputStyle}
          />
        </div>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ ...th(), padding: '6px 10px' }}>Producto</th>
                <th style={{ ...th('right'), padding: '6px 10px', width: 92 }}>Homologables</th>
              </tr>
            </thead>
            <tbody>
              {visibleTcoat.map((item) => {
                const active = Number(item.id) === Number(selectedTcoatId);
                const count = (matchesByTcoat.get(Number(item.id)) || []).length;
                const compactTd = (align = 'left') => ({
                  ...td(align),
                  padding: '7px 10px',
                  lineHeight: 1.3,
                  whiteSpace: 'nowrap',
                });
                return (
                  <tr
                    key={item.id}
                    onClick={() => {
                      setSelectedTcoatId(item.id);
                      setCompetitorQuery('');
                      setLinkError('');
                    }}
                    style={{
                      cursor: 'pointer',
                      background: active ? selectedBg : 'transparent',
                      boxShadow: active ? 'inset 3px 0 0 #16a34a' : 'none',
                    }}
                  >
                    <td
                      style={{ ...compactTd(), overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={`${item.sku || ''} ${item.product_name || ''}`.trim()}
                    >
                      <span style={{ fontWeight: 700 }}>{item.sku || '—'}</span>
                      {item.product_name ? (
                        <span style={{ color: ui.muted }}> · {item.product_name}</span>
                      ) : null}
                    </td>
                    <td style={{ ...compactTd('right'), fontWeight: 700, color: count ? '#16a34a' : ui.muted }}>
                      {count}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!visibleTcoat.length ? (
            <p style={{ margin: 0, padding: 16, fontSize: 12, color: ui.muted }}>No hay productos T-COAT.</p>
          ) : null}
        </div>
      </section>

      <section style={box}>
        <div style={boxHeader}>
          <div style={{ minWidth: 0 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text }}>
              {selectedTcoat ? `${selectedTcoat.sku || ''} ${selectedTcoat.product_name || ''}`.trim() : 'Competencia'}
              {selectedCompetitor ? ` ↔ ${selectedCompetitor.brand} ${selectedCompetitor.product_name}` : ''}
            </h4>
            <span style={{ fontSize: 12, color: linked ? '#16a34a' : ui.muted, fontWeight: linked ? 700 : 400 }}>
              {selectedTcoat
                ? linked
                  ? relationLabel(selectedRelation) || 'Homologados'
                  : selectedCompetitor
                    ? 'Pendiente de vincular'
                    : 'Elige un producto de la competencia'
                : 'Selecciona un producto T-COAT'}
            </span>
          </div>
          {selectedTcoat && selectedCompetitor && access.canUpdate ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {!linked ? (
                <RelationSelect
                  value={draftRelation}
                  onChange={setDraftRelation}
                  disabled={isLinking}
                  isDarkMode={isDarkMode}
                />
              ) : null}
              <button type="button" onClick={toggleHomologation} disabled={isLinking} style={linked ? ghostBtn : primaryBtn}>
                {linked ? <Unlink size={14} /> : <Link2 size={14} />}
                {isLinking ? 'Guardando…' : linked ? 'Desvincular' : 'Homologar'}
              </button>
            </div>
          ) : null}
        </div>
        <div style={{ padding: '10px 12px', borderBottom: `1px solid ${ui.border}` }}>
          <input
            value={competitorQuery}
            onChange={(event) => setCompetitorQuery(event.target.value)}
            placeholder="Buscar homologable o escribir para añadir otro producto de la competencia…"
            style={inputStyle}
            disabled={!selectedTcoat}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateRows: 'minmax(0, 1fr) minmax(160px, 0.9fr)', minHeight: 0, flex: 1 }}>
          <div style={{ overflow: 'auto', borderBottom: `1px solid ${ui.border}` }}>
            {visibleCompetitors.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th()}>Producto</th>
                    <th style={th()}>Categoría</th>
                    <th style={th()}>Química</th>
                    <th style={th()}>Precio</th>
                    <th style={th()}>Relación</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCompetitors.map((item) => {
                    const active = Number(item.id) === Number(selectedCompetitorId);
                    const match = matchForPair(selectedTcoatId, item.id);
                    const isLinked = Boolean(match);
                    const relation = match?.relation || relationFor(item.id);
                    return (
                      <tr
                        key={item.id}
                        onClick={() => setSelectedCompetitorId(item.id)}
                        style={{
                          cursor: 'pointer',
                          background: active ? selectedBg : 'transparent',
                        }}
                      >
                        <td style={td()}>
                          <div style={{ fontWeight: 700 }}>{item.product_name}</div>
                          <div style={{ fontSize: 11, color: ui.muted }}>{item.brand || '—'}</div>
                        </td>
                        <td style={td()}>{item.category || '—'}</td>
                        <td style={td()}>{item.chemistry || '—'}</td>
                        <td style={td()}>
                          <div style={{ fontWeight: 600 }}>{item.priceRangeLabel || '—'}</div>
                          {item.distributor_count ? (
                            <div style={{ fontSize: 11, color: ui.muted }}>
                              {item.distributor_count} dist.
                            </div>
                          ) : null}
                        </td>
                        <td style={td()}>
                          {isLinked ? (
                            access.canUpdate ? (
                              <RelationSelect
                                value={relation || 'homologo'}
                                disabled={isLinking}
                                isDarkMode={isDarkMode}
                                onChange={(next) => changeRelation(match.id, next)}
                              />
                            ) : (
                              <RelationBadge value={relation || 'homologo'} isDarkMode={isDarkMode} />
                            )
                          ) : (
                            <span style={{ color: ui.muted, fontWeight: 600 }}>Candidato</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p style={{ margin: 0, padding: 16, fontSize: 12, color: ui.muted }}>
                {selectedTcoat
                  ? competitorQuery
                    ? 'No hay productos que coincidan con la búsqueda.'
                    : 'No hay homologables. Escribe en el buscador para encontrar un producto de la competencia y vincularlo.'
                  : 'Selecciona un producto T-COAT a la izquierda.'}
              </p>
            )}
          </div>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th()}>Campo</th>
                  <th style={th()}>T-COAT</th>
                  <th style={th()}>Competencia</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.label}>
                    <td style={{ ...td(), color: ui.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.label}</td>
                    <td style={td()}>{row.left || '—'}</td>
                    <td style={td()}>{row.right || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {selectedCompetitor?.summary ? (
              <p style={{ margin: 0, padding: '10px 12px', fontSize: 12, color: ui.muted, lineHeight: 1.45 }}>
                {selectedCompetitor.summary}
              </p>
            ) : null}
            {selectedCompetitor?.distributors?.length ? (
              <div style={{ padding: '0 12px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Distribuidores del producto
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {selectedCompetitor.distributors.map((row, index) => (
                    <div
                      key={`${row.id || row.name}-${index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: `1px solid ${ui.border}`,
                        background: ui.header,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: ui.text }}>{row.name || 'Distribuidor'}</div>
                        {row.website_url ? (
                          <a
                            href={row.website_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            style={{ fontSize: 11, color: ui.interactive || '#16a34a', display: 'inline-flex', gap: 4, alignItems: 'center' }}
                          >
                            Ver página <ExternalLink size={11} />
                          </a>
                        ) : null}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: ui.text, whiteSpace: 'nowrap' }}>
                        {formatMoneyClp(row.sale_price) || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {linkError ? <p style={{ margin: 0, padding: '8px 12px', fontSize: 12, color: '#dc2626' }}>{linkError}</p> : null}
      </section>
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          <Swords size={22} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Competidores
        </Heading>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: ui.muted }}>
          Catálogo de la competencia y cuadro comparativo de homologación con T-COAT.
        </p>
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.panel,
        }}
      >
        <ProductionInnerTabs
          tabs={[
            { key: 'products', label: 'Productos', count: products.length },
            { key: 'compare', label: 'Comparativa', count: matches.length },
          ]}
          active={tab}
          onChange={setTab}
        />
        <div style={{ padding: 12 }}>
          {error ? <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 8 }}>{error}</div> : null}
          {isLoading ? <div style={{ fontSize: 13, color: ui.muted }}>Cargando competidores…</div> : null}
          {!isLoading && tab === 'products' ? renderProductsTab() : null}
          {!isLoading && tab === 'compare' ? renderCompareTab() : null}
        </div>
      </div>

      <FormDrawer
        isOpen={formOpen}
        onClose={() => !isSaving && setFormOpen(false)}
        onSave={saveForm}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        title={editingId ? 'Editar producto competidor' : 'Nuevo producto competidor'}
        subtitle="Pinturas · Competidores"
        sections={formSections}
        formData={form}
        onChange={(key, value) => setForm((prev) => ({ ...prev, [key]: value }))}
        formError={formError}
        saveLabel={editingId ? 'Guardar' : 'Crear'}
        readOnly={editingId ? !access.canUpdate : !access.canCreate}
        width={720}
      />
    </div>
  );
};

export default PaintCompetitorsWorkspace;
